import { defineNuxtPlugin } from 'nuxt/app'

// Define types for Vue instance augmentation
declare module 'nuxt/app' {
  interface NuxtApp {
    $loadScriptWithPromise: (
      src: string,
      id: string,
      attributes?: Record<string, string>,
    ) => Promise<void>
    $unloadScript: (id: string) => void
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $loadScriptWithPromise: (
      src: string,
      id: string,
      attributes?: Record<string, string>,
    ) => Promise<void>
    $unloadScript: (id: string) => void
  }
}

/**
 * Removes a previously loaded script from the DOM
 * @param id - The unique ID of the script element to remove
 */
function unloadScript(id: string) {
  const el = document.querySelector(`#${id}`)
  if (!el) {
    return
  }
  document.head.removeChild(el)
}

/**
 * Dynamically loads an external JavaScript file
 *
 * IMPORTANT: Callers MUST handle Promise rejections to prevent unhandled errors.
 * Scripts can fail for multiple reasons:
 * - Content blockers (privacy extensions blocking analytics/social scripts)
 * - Network errors (timeout, DNS failure, CORS)
 * - CSP violations (Content Security Policy restrictions)
 * - 404/403 responses from the server
 *
 * @param src - The URL of the script to load
 * @param id - Unique identifier for the script element (prevents duplicate loads)
 * @param attributes - Optional HTML attributes to apply to the script element
 * @returns Promise that resolves when script loads successfully, rejects with Error on failure
 *
 * @example
 * // Handle script loading errors
 * try {
 *   await this.$loadScriptWithPromise('https://example.com/sdk.js', 'example-sdk')
 * } catch (error) {
 *   console.warn('Failed to load SDK:', error)
 *   // Hide the feature or show alternative UI
 * }
 */
function loadScriptWithPromise(
  src: string,
  id: string,
  attributes?: Record<string, string>,
): Promise<void> {
  if (document.getElementById(id)) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = id
    script.src = src
    script.async = true
    script.defer = true

    // Apply any additional attributes
    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        script.setAttribute(key, value)
      })
    }

    script.onload = () => resolve()
    script.onerror = (e: Event | string) => {
      // Create a proper Error object for better error handling
      const error = new Error(
        `Failed to load script: ${src}${
          typeof e === 'string' ? ` - ${e}` : ''
        }`,
      )
      // Preserve the original event as a property for debugging
      ;(error as any).originalEvent = e
      reject(error)
    }
    document.head.appendChild(script)
  })
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      loadScriptWithPromise,
      unloadScript,
    },
  }
})
